/**
 * Who did it, in words (N32).
 *
 * Activity named every actor "a person (87ad2b17)": ProGST holds no user directory, and keeping
 * one would be the shadow copy constitution §1 forbids. The platform already answers the
 * question — the org service lists an organisation's members, with their names, to every
 * member (`GET /api/v1/orgs/{org}/members`) — so the screen asks it at render time, through
 * the same edge and the same sign-in as the org switcher, and keeps nothing.
 *
 * The id is never shown. Where the list cannot be read, the words say less, not the id.
 */

import { ORGS_API } from './config'
import { session } from './session'

export interface Member {
  user_id: string
  full_name: string | null
  email: string | null
}

/** The organisation's current members, or null when the list cannot be read. */
export async function fetchMembers(orgId: string | null): Promise<Member[] | null> {
  if (!orgId || !session.getSession()) return null
  try {
    const res = await session.authed(`${ORGS_API}/${encodeURIComponent(orgId)}/members`)
    if (!res.ok) return null
    const body = (await res.json()) as { data?: { members?: Member[] } }
    return body.data?.members ?? null
  } catch {
    // Unreachable is not a reason to show an id, nor to fail the screen.
    return null
  }
}

/**
 * The actor of one event. `members` null means the list could not be read; `me` is the
 * signed-in person's id.
 */
export function actorWords(
  event: { actor_kind: string; actor_user_id?: string | null },
  members: readonly Member[] | null,
  me: string | null,
): string {
  if (event.actor_kind === 'system') return 'ProGST, automatically'
  // An approval through the client's link (FR-E15). It read "ProGST, automatically", which
  // put the client's approval in the app's name.
  if (event.actor_kind === 'client') return 'the client, through the approval link'
  if (event.actor_kind !== 'user') return 'a kind of actor ProGST has no words for'
  const id = event.actor_user_id ?? null
  if (id && me && id === me) return 'you'
  if (members === null || !id) return 'a member of this firm'
  const member = members.find((m) => m.user_id === id)
  if (!member) return 'someone no longer in this firm'
  // The owner's choice (2026-10-23): with no name on the platform, the email members already
  // see of each other in the console.
  return member.full_name?.trim() || member.email?.trim() || 'a member of this firm'
}
